import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchSorter } from 'match-sorter';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Link from '../../../shared/_TBComponents/Link';
import Text from '../../../shared/_TBComponents/Text';
import hexToRgba from '../../../shared/utils/hexToRgba';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import NavigatorPreviewPanel from './presentational/NavigatorPreviewPanel';
import NavigatorSelectorPanel from './presentational/NavigatorSelectorPanel';
import { fetchProgramPreview } from '../ducks/navigatorActions';
import { fetchCalendars, createCalendar } from '../../calendars/ducks/calendarsActions';
import {
  setActiveModal,
  setSelectedSavedWorkout,
  setSelectedReduxCalendar,
  setSelectedReduxAthlete,
  setSelectedReduxNonDateBasedProgram,
  setProgramType,
  setSelectedDate,
} from '../../calendar/ducks/calendarActions';
import {
  fetchPendingImports,
  abandonImport,
  loadImportForReview,
  clearLastImportedProgramId,
} from '../../ai-program-import/ducks';

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-right: none;
  padding: 16px;
  gap: 8px;
  overflow-y: auto;
  box-shadow: inset 8px 0 12px -12px rgba(0,0,0,0.18);
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 1000px;
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

// Wrapper that flips the icomoon icon inside the Load to Calendar button
// vertically. The icomoon set has a `download` glyph (down-arrow over a
// horizontal line) but no matching upload — flipping it gives the
// upload-arrow visual Figma 7106:3150 asks for without shipping a new
// asset. Targets the inner .icomoon-svg class the Button component
// renders.
const FlipIconWrap = styled.div`
  display: inline-flex;

  .icomoon-svg {
    transform: scaleY(-1);
  }
`;

// Load to Calendar modal — values pulled from Figma node 7112:3447 via
// the Figma MCP. 470px box with 4px radius, 32px padding, 32px gap between
// header / field / field / footer blocks. Items-end so the footer hugs the
// right edge.
const CalPickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

const CalPickerBox = styled.div`
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 32px;
  width: 470px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 32px;
  box-sizing: border-box;
`;

// Header block stacks title row + subtitle with an 8px gap (Figma 7107:3482).
const CalPickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const CalPickerTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

// Title — Nunito Sans Black (900), 24px, #444 per Figma node 7107:3483.
const CalPickerTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 24px;
  font-weight: 900;
  color: #444444;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;
  white-space: nowrap;
`;

// Close button frames the 32x32 close icon (visible inner glyph ~16x16).
const CalPickerClose = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin: -4px;
  width: 32px;
  height: 32px;
  color: #444444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: #F4F4F8;
  }
`;

// Subtitle — Regular 16/1.5 #9E9E9E per Figma node 7107:3484.
const CalPickerSubtitle = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

// Field column — Figma uses a 2px gap between label and input
// (nodes 7107:3463 / 7107:3464).
const CalPickerField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

// Label — Bold 16/1.5 #444 per Figma node I7107:3463;495:17720;0:9332.
const CalPickerLabel = styled.label`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  line-height: 1.5;
`;

// Searchable combobox shell — same border / radius / padding as the legacy
// <select> but with room on the LEFT for a search-icon affordance and on
// the right for a chevron (Figma node 7119:4167). The input itself fills
// the middle; the wrapper owns the border + focus ring so a focused input
// and a focused dropdown stay visually unified.
const CalPickerComboboxWrap = styled(({ isOpen, ...rest }) => <div {...rest} />)`
  position: relative;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid ${(props) => (props.isOpen ? '#444444' : '#E0E0E0')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 6px 8px 6px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${(props) => (props.isOpen ? '0 0 0 3px rgba(68, 68, 68, 0.12)' : 'none')};

  &:hover {
    border-color: #C9C9D4;
  }
`;

// Search icon — left-aligned magnifying glass per Figma 7119:5796. Muted
// #9E9E9E when nothing is selected, #444 once the field is populated.
const CalPickerSearchIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  margin-right: 8px;
`;

const CalPickerComboboxInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;

  &::placeholder {
    color: #9E9E9E;
  }
`;

// Chevron-down on the right; rotates 180° when the dropdown is open.
const CalPickerChevron = styled(({ isOpen, ...rest }) => <span {...rest} />)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #444444;
  pointer-events: none;
  transition: transform 0.18s ease;
  transform: rotate(${(props) => (props.isOpen ? '180deg' : '0deg')});
`;

// Floating dropdown panel — absolutely positioned beneath the input. White
// surface, 1px #E0E0E0 border, 4px radius, capped height with native scroll
// so a long calendar list never blows past the modal. No vertical padding:
// the group labels carry their own tinted-band background and need to run
// edge-to-edge inside the panel.
const CalPickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  box-shadow: 0 12px 24px rgba(48, 49, 51, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 10;
  padding: 0;
`;

// Section header — Figma calls for uppercase 12/700 #9E9E9E. To make the
// boundary between CALENDARS and ATHLETE CALENDARS unmissable, we adopt the
// planner's GroupHeader treatment: a tinted #F9F9F9 band with a 1px #EEE
// bottom border. This is the same pattern coaches already see in Coach
// Tools → Planner so it reads as familiar grouping, not a stray label.
const CalPickerDropdownGroupLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.36px;
  color: #9E9E9E;
  text-transform: uppercase;
  padding: 8px 16px;
  background-color: #F9F9F9;
  border-bottom: 1px solid #EEEEEE;
`;

const CalPickerDropdownOption = styled(({ isFocused, ...rest }) => (
  <button type='button' {...rest} />
))`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(props) => (props.isFocused ? '#F4F4F6' : 'transparent')};
  border: none;
  padding: 8px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    background: #F4F4F6;
  }
`;

const CalPickerDropdownEmpty = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #9E9E9E;
  padding: 12px 16px;
  text-align: center;
`;

// Text input — Figma uses pl-16 pr-56 py-6 (the right padding makes room
// for a clear/dropdown affordance that we don't render here).
const CalPickerInput = styled.input`
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 6px 56px 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #9E9E9E;
  }

  &:hover:not(:disabled) {
    border-color: #C9C9D4;
  }

  &:focus,
  &:focus-visible {
    border-color: #444444;
    box-shadow: 0 0 0 3px rgba(68, 68, 68, 0.12);
  }
`;

const CalPickerError = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #C43D32;
  margin: 0;
  width: 100%;
`;

// Footer row — 16px gap between buttons per Figma node 7119:4151.
const CalPickerFooter = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

// Cancel — Figma node 7119:4152: 2px #EEE border, transparent bg, 4px
// radius, Bold 13/0.39 tracking #444 text.
const CalPickerCancel = styled.button`
  background: transparent;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover {
    border-color: #C9C9D4;
    background: #FAFAFB;
  }
`;

// CONTINUE — Figma node 7119:4143 (disabled) + 7119:5807 (enabled). Same
// shell, just swaps bg + text color tokens. Gap of 8px between label and
// arrow icon.
const CalPickerContinue = styled.button`
  background: ${(p) => (p.disabled ? '#E0E0E0' : '#444444')};
  color: ${(p) => (p.disabled ? '#757575' : '#FFFFFF')};
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  text-transform: uppercase;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ProgramItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  height: 100%;
  max-width: 325px;
`;

const StickySectionHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 8px 0 6px 0;
`;

// Pending imports read as plain rows in the sidebar — same visual language
// as ProgramItem so the eye can scan the column without context-switching
// to "yellow = warning". The yellow live in the banner on the right side
// once the row is selected, which is enough signal that it's unfinished.
const PendingItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

// Pending-import detail layout per Figma node 7106:4786 — banner +
// button row stacked with 8px gap, 406px target width.
const PendingDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 406px;
`;

// White card that backs the pending detail content, matching the surface
// the regular program detail renders on (Figma node 7106:4734). 16px
// radius, 1px #EEE border, 24px padding all sides. flex:1 + min-height so
// the card claims the same vertical real estate as the populated program
// detail does — otherwise the card collapses to its content height and
// reads as floating mid-page.
const PendingDetailCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  flex: 1;
  min-height: 600px;
  box-sizing: border-box;
`;

// Post-import green success banner (Figma node 7164:1505). Tinted success
// surface, 1px theme/success/emphasis border, 4px radius, 16px h-padding /
// Post-import green success banner (Figma node 7164:1505). Tinted success
// surface, 1px theme/success/emphasis border, 4px radius, 16px h-padding /
// 8px v-padding. Only renders when the LD flag `aiImportHomeLanding` is
// ON — the wizard stashes lastImportedProgramId in that path. Width capped
// to match the HeaderContainer so it never extends past the title row.
const ImportSuccessBanner = styled.div`
  background: rgba(13, 204, 138, 0.12);
  border: 1px solid #0DCC8A;
  border-radius: 4px;
  padding: 8px 16px;
  margin-bottom: 12px;
  max-width: 1000px;
  width: 100%;
  box-sizing: border-box;
`;

const ImportSuccessBannerBody = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #0E7F5A;
  margin: 0;
  line-height: 1.5;
`;

// Yellow banner from Figma node 7106:4787: rgba(255,203,71,0.2) bg,
// 1px #FFCB47 border, 4px radius, 16px padding all around.
const PendingBanner = styled.div`
  background: rgba(255, 203, 71, 0.2);
  border: 1px solid #FFCB47;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
`;

// Body text per Figma node 7106:4789: Bold 12px #444, line-height 1.5,
// w-full inside the banner.
const PendingBannerBody = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #444444;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

// Pending-import button row — Figma node 7106:4790: 8px gap, items-center.
const PendingButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Delete button per Figma node 7106:4791: transparent bg, 2px #EEE border,
// 4px radius, 16px h-padding / 6px v-padding, 8px icon→text gap.
// Text is #444 (default fg) — only the trash glyph carries the red.
const PendingDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover:not(:disabled) {
    border-color: #C9C9D4;
    background: #FAFAFB;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Review button per Figma node 7106:4792: bg #444, white text, 4px radius,
// 16px h-padding / 6px v-padding, text first then arrow icon (8px gap).
const PendingReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #444444;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Delete-import confirm modal. Reuses CalPickerOverlay (the same shaded
// backdrop pattern as the Load to Calendar modal) so dialog chrome stays
// consistent. The card itself is narrower (420px) since the body is just
// one short paragraph + two buttons — no input fields to land on.
const ConfirmDeleteCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 24px;
  width: 420px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
`;

const ConfirmDeleteTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #444444;
  margin: 0;
  line-height: 1.4;
`;

const ConfirmDeleteBody = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #616161;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmDeleteFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

// Cancel — neutral outlined button, matches the existing PendingDelete
// button shell so the dialog reads as part of the same surface.
const ConfirmDeleteCancel = styled.button`
  padding: 6px 16px;
  background: transparent;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover:not(:disabled) {
    border-color: #C9C9D4;
    background: #FAFAFB;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Destructive primary — theme/error/emphasis (#FF0505) bg, white text.
// The destructive colour signals what the action does without needing
// extra copy in the body.
const ConfirmDeleteConfirm = styled.button`
  padding: 6px 16px;
  background: #FF0505;
  border: none;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease;

  &:hover:not(:disabled) {
    background: #D70000;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ArrowLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #8EB8FF;
  }
`;

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  padding: 12px 16px 4px;
  flex-shrink: 0;
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileSectionTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #444;
  padding: 6px 0 4px;
`;

const MobileDetailContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
  min-height: 0;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #999; }
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const ProgramNavigator = ({
  accountCode, programId, manageUsers, isMobile,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountPrimaryColor = theme?.colorPrimary || currentUser?.userSkin?.primary || '#77A7FF';
  const selectedProgramBg = hexToRgba(accountPrimaryColor, 0.05);

  const programs = useSelector((state) => state.navigator.data.programs);
  const programsError = useSelector((state) => state.navigator.data.programsError);
  const isLoading = useSelector((state) => state.navigator.ui.isLoadingPrograms);
  const unparsedCalendars = useSelector((state) => state.calendars.data.unparsedCalendars || []);
  const createdTopCalId = useSelector((state) => state.calendars.data.createdTopCalId);
  const pendingImports = useSelector((state) => state.aiProgramImport.pendingImports || []);
  const isAbandoning = useSelector((state) => state.aiProgramImport.isAbandoning);
  const isLoadingHistoryDetail = useSelector(
    (state) => state.aiProgramImport.isLoadingHistoryDetail,
  );
  // Stays null unless the LD flag `aiImportHomeLanding` is ON and the
  // wizard just landed the coach here after a confirm.
  const lastImportedProgramId = useSelector(
    (state) => state.aiProgramImport.lastImportedProgramId,
  );

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedPendingId, setSelectedPendingId] = useState(null);
  const [calPickerOpen, setCalPickerOpen] = useState(false);
  // Combobox state. The selected calendar is identified by a typed key
  //   `cal-{id}` for an account calendar
  //   `athlete-{userId}` for an athlete calendar
  // so the Continue handler can route accordingly. `calSearch` is the input
  // text — when no calendar is selected, it's the filter query; once a
  // calendar is picked, it shows the calendar name.
  const [selectedCalKey, setSelectedCalKey] = useState('');
  const [calSearch, setCalSearch] = useState('');
  const [calDropdownOpen, setCalDropdownOpen] = useState(false);
  const [planCalendars, setPlanCalendars] = useState({ account: [], athlete: [] });
  const [isLoadingPlanCalendars, setIsLoadingPlanCalendars] = useState(false);
  const [newCalName, setNewCalName] = useState('');
  const calComboboxRef = useRef(null);
  const [calError, setCalError] = useState('');
  const [pendingLoad, setPendingLoad] = useState(false);
  // Confirm modal for the trash button. Holds the import id we're about
  // to delete so it survives the modal mount even if the user clicks
  // through to a different pending import after opening the dialog.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const isInvalidProgramId = !!programId
    && !isLoading
    && programs?.length > 0
    && !programs.some((p) => String(p.id) === String(programId));

  // Find program by programId from URL
  useEffect(() => {
    if (programId && programs.length > 0) {
      const found = programs.find((p) => String(p.id) === String(programId));
      setSelectedProgram(found || null);
    } else if (!programId) {
      setSelectedProgram(null);
    }
  }, [programId, programs]);

  useEffect(() => {
    if (selectedProgram && accountCode) {
      dispatch(fetchProgramPreview(accountCode, selectedProgram.id));
    }
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchPendingImports());
    }
  }, [accountCode]);

  // Selecting a saved program clears any pending selection so the panels
  // don't fight over the main content area.
  useEffect(() => {
    if (selectedProgram) setSelectedPendingId(null);
  }, [selectedProgram?.id]);

  const selectedPendingImport = pendingImports.find(
    (p) => String(p.id) === String(selectedPendingId),
  ) || null;

  const handleSelectPending = useCallback((historyId) => {
    setSelectedPendingId(historyId);
    if (programId) {
      // Drop the program param from the URL so the saved-program detail
      // doesn't re-render under the pending detail.
      history.push('/dashboard/programs');
    }
  }, [programId, history]);

  const handleReviewPending = useCallback(async () => {
    if (!selectedPendingId) return;
    const result = await dispatch(loadImportForReview(selectedPendingId));
    if (result?.success) {
      history.push('/import/program');
    }
  }, [dispatch, history, selectedPendingId]);

  // Trash button now opens a confirm dialog instead of immediately
  // abandoning. Abandon is destructive and not undoable on the backend, so
  // the extra click protects against fat-fingering on the navigator pane.
  const handleAbandonPending = useCallback(() => {
    if (!selectedPendingId) return;
    setPendingDeleteId(selectedPendingId);
  }, [selectedPendingId]);

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    const result = await dispatch(abandonImport(pendingDeleteId));
    if (result?.success) {
      // Clear both the modal id and the selection so the navigator falls
      // back to its empty / programs view after the row disappears.
      if (String(pendingDeleteId) === String(selectedPendingId)) {
        setSelectedPendingId(null);
      }
      setPendingDeleteId(null);
    }
  }, [dispatch, pendingDeleteId, selectedPendingId]);

  useEffect(() => {
    if (calPickerOpen && accountCode) {
      dispatch(fetchCalendars(accountCode));
    }
  }, [calPickerOpen, accountCode]);

  // Fetch the typeahead calendar list from /plans/calendars. Same endpoint
  // the planner module (CalendarChooser) uses, so the response shape is
  // already trusted and athlete-aware. Only fires while the modal is open.
  useEffect(() => {
    if (!calPickerOpen || !accountCode) return undefined;
    let cancelled = false;
    setIsLoadingPlanCalendars(true);
    axiosAuthed
      .get(`/accounts/${accountCode}/plans/calendars`)
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setPlanCalendars({
          account: Array.isArray(data.accountCalendars) ? data.accountCalendars : [],
          athlete: Array.isArray(data.athleteCalendars) ? data.athleteCalendars : [],
        });
        setIsLoadingPlanCalendars(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoadingPlanCalendars(false);
      });
    return () => { cancelled = true; };
  }, [calPickerOpen, accountCode]);

  // Click-outside dismissal for the dropdown panel. Closes the panel only,
  // not the whole modal — clicking elsewhere inside the modal still works.
  useEffect(() => {
    if (!calDropdownOpen) return undefined;
    const handler = (e) => {
      if (calComboboxRef.current && !calComboboxRef.current.contains(e.target)) {
        setCalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [calDropdownOpen]);

  const goToLoadModal = useCallback((calendarId) => {
    const calendar = unparsedCalendars.find((c) => c.id === calendarId) || { id: calendarId };
    dispatch(setSelectedDate(new Date()));
    dispatch(setProgramType('calendar'));
    dispatch(setSelectedReduxCalendar(calendar));
    if (selectedProgram) {
      dispatch(setSelectedSavedWorkout(selectedProgram));
    }
    history.push('/build');
    setTimeout(() => dispatch(setActiveModal('loadProgramsModal')), 300);
  }, [unparsedCalendars, selectedProgram, dispatch, history]);

  useEffect(() => {
    if (pendingLoad && createdTopCalId) {
      setPendingLoad(false);
      goToLoadModal(createdTopCalId);
    }
  }, [createdTopCalId, pendingLoad, goToLoadModal]);

  // Auto-dismiss the post-import success banner. Two triggers:
  //   1. The selected program no longer matches the just-imported id (the
  //      coach navigated away — the banner shouldn't follow them).
  //   2. A 6s timer so the banner doesn't linger if they sit on the page.
  // The state is non-persisted so a refresh also clears it naturally. No-
  // op when lastImportedProgramId is null (LD flag OFF or no recent
  // import).
  useEffect(() => {
    if (!lastImportedProgramId) return undefined;
    if (selectedProgram && String(selectedProgram.id) !== String(lastImportedProgramId)) {
      dispatch(clearLastImportedProgramId());
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(clearLastImportedProgramId());
    }, 6000);
    return () => clearTimeout(timer);
  }, [lastImportedProgramId, selectedProgram, dispatch]);

  const handleOpenLoadCalendar = useCallback(() => {
    setSelectedCalKey('');
    setCalSearch('');
    setCalDropdownOpen(false);
    setNewCalName('');
    setCalError('');
    setCalPickerOpen(true);
  }, []);

  // matchSorter filters across the visible label only; case-insensitive,
  // word-order-tolerant. Empty query returns the full list so the dropdown
  // is useful on first focus.
  const filteredCalOptions = useMemo(() => {
    const accountOptions = planCalendars.account.map((cal) => ({
      key: `cal-${cal.id}`,
      type: 'calendar',
      id: cal.id,
      label: cal.name || `Calendar ${cal.id}`,
      raw: cal,
    }));
    const athleteOptions = planCalendars.athlete.map((athlete) => ({
      key: `athlete-${athlete.id}`,
      type: 'athlete',
      id: athlete.id,
      label: athlete.name || `User ${athlete.id}`,
      raw: athlete,
    }));
    const trimmed = calSearch.trim();
    // If a calendar is selected and the input mirrors its label, treat the
    // search as empty — otherwise the dropdown filters down to just the
    // selected option and reads as "no other choices".
    const selected = [...accountOptions, ...athleteOptions]
      .find((opt) => opt.key === selectedCalKey);
    const queryActive = trimmed && (!selected || selected.label !== trimmed);
    const filter = (list) => (queryActive ? matchSorter(list, trimmed, { keys: ['label'] }) : list);
    return {
      account: filter(accountOptions),
      athlete: filter(athleteOptions),
    };
  }, [planCalendars, calSearch, selectedCalKey]);

  // Mutual exclusion per the designer's note:
  //   "if you type in 'create a new calendar', we set 'select a calendar'
  //    back to blank. and vice versa."
  const handlePickCalendar = useCallback((option) => {
    if (newCalName) setNewCalName('');
    setSelectedCalKey(option.key);
    setCalSearch(option.label);
    setCalDropdownOpen(false);
    setCalError('');
  }, [newCalName]);

  const handleSearchChange = useCallback((value) => {
    // Typing into the search clears any previous selection — the input is
    // the source of truth while the dropdown is open.
    if (selectedCalKey) setSelectedCalKey('');
    setCalSearch(value);
    setCalDropdownOpen(true);
    setCalError('');
  }, [selectedCalKey]);

  const handleTypeNew = useCallback((value) => {
    if (selectedCalKey) {
      setSelectedCalKey('');
      setCalSearch('');
    }
    setNewCalName(value);
    setCalError('');
  }, [selectedCalKey]);

  // One Continue handler dispatches whichever path is populated. Disabled
  // state on the button is the primary guard; this just routes.
  const handleContinue = useCallback(() => {
    if (selectedCalKey) {
      const selected = [
        ...planCalendars.account.map((cal) => ({ key: `cal-${cal.id}`, type: 'calendar', raw: cal })),
        ...planCalendars.athlete.map((a) => ({ key: `athlete-${a.id}`, type: 'athlete', raw: a })),
      ].find((opt) => opt.key === selectedCalKey);
      if (!selected) return;
      setCalError('');
      setCalPickerOpen(false);
      if (selected.type === 'athlete') {
        // Route athlete picks through the athlete-calendar slice — the
        // downstream LoadProgramsModal already branches on whether
        // selectedReduxAthlete or selectedReduxCalendar is populated.
        dispatch(setSelectedDate(new Date()));
        dispatch(setProgramType('calendar'));
        dispatch(setSelectedReduxAthlete(selected.raw));
        if (selectedProgram) {
          dispatch(setSelectedSavedWorkout(selectedProgram));
        }
        history.push('/build');
        setTimeout(() => dispatch(setActiveModal('loadProgramsModal')), 300);
      } else {
        goToLoadModal(Number(selected.raw.id));
      }
      return;
    }
    if (newCalName.trim()) {
      setCalError('');
      setPendingLoad(true);
      dispatch(createCalendar({
        accountCode,
        body: { name: newCalName.trim(), workoutVisibility: 0 },
      }));
    }
  }, [
    selectedCalKey, planCalendars, newCalName, goToLoadModal,
    accountCode, dispatch, selectedProgram, history,
  ]);

  const handleEditProgram = useCallback(() => {
    if (!selectedProgram) return;
    dispatch(setProgramType('free-form-program'));
    dispatch(setSelectedReduxNonDateBasedProgram(selectedProgram));
    history.push('/build');
  }, [selectedProgram, dispatch, history]);

  const selectorButtons = [
    ...(manageUsers ? [
      {
        name: 'Add',
        icon: 'add-program',
        iconSize: 20,
        iconColor: '#9E9E9E',
        href: '/program/create',
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            history.push('/program/create');
          }
        },
      },
      {
        name: 'Manage',
        href: `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/exercises?action=savedPrograms`,
        onClick: (e) => {
          const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          if (!isModified) {
            e.preventDefault();
            window.location.href = `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/exercises?action=savedPrograms`;
          }
        },
      },
    ] : []),
  ];

  const renderProgramsInfo = ({ showTitle = true } = {}) => (
    <>
      {showTitle && (
      <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
        Programs
      </Text>
      )}
      <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
        Programs have relative days (Day 1, Day 2) instead of real dates (April 10, April 11).
      </Text>
      <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
        You can assign them as self-paced workouts or load them into calendars.
      </Text>
      <ArrowLink
        href='https://support.teambuildr.com/article/xyf01bviza-free-form-programming-overview'
        target='_blank'
        fontSize='18px'
        color='#77A7FF'
        fontWeight={700}
      >
        More about programs
        <Icon icon='alt-right-arrow' size={10} color='currentColor' />
      </ArrowLink>
    </>
  );

  const renderProgramList = ({ selectedRef, searchFilter }) => {
    const filtered = searchFilter && programs
      ? matchSorter(programs, searchFilter, { keys: ['programName'] })
      : programs;

    // Pending imports are filtered with the same search term so the group
    // collapses naturally when the coach is hunting for a specific program.
    // When there's no active search we render the list alphabetically by
    // display name; with a search active we defer to matchSorter's
    // relevance ordering so the closest match floats to the top.
    const pendingDisplayName = (it) => (
      it.programName || it.originalProgramName || 'Untitled import'
    );
    const filteredPending = searchFilter
      ? matchSorter(pendingImports, searchFilter, { keys: ['programName', 'originalProgramName'] })
      : [...pendingImports].sort((a, b) => pendingDisplayName(a).localeCompare(
        pendingDisplayName(b),
        undefined,
        { sensitivity: 'base' },
      ));
    const hasPending = filteredPending.length > 0;

    if (programsError && !searchFilter && !hasPending) {
      return (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {programsError}
        </Text>
      );
    }

    const programsContent = !filtered || filtered.length === 0
      ? (
        <Text styledTextProps={{ fontSize: '14px', color: '#666' }}>
          {searchFilter ? 'No programs match your search' : 'No programs found'}
        </Text>
      ) : filtered.map((program) => (
        <ProgramItem
          key={program.id}
          isSelected={selectedProgram?.id === program.id}
          bgColor={accountPrimaryColor}
          selectedBg={selectedProgramBg}
          ref={selectedProgram?.id === program.id ? selectedRef : null}
          onClick={() => history.push(`/dashboard/programs/${program.id}`)}
        >
          <Text
            styledTextProps={{
              fontSize: '16px',
              fontWeight: selectedProgram?.id === program.id ? 700 : 400,
            }}
          >
            {program.programName}
          </Text>
        </ProgramItem>
      ));

    return (
      <>
        {hasPending && (
          <>
            <StickySectionHeader data-sticky-header>Unfinished Imports</StickySectionHeader>
            {filteredPending.map((item) => {
              const name = item.programName || item.originalProgramName || 'Untitled import';
              const isSelected = String(item.id) === String(selectedPendingId);
              return (
                <PendingItem
                  key={item.id}
                  isSelected={isSelected}
                  bgColor={accountPrimaryColor}
                  selectedBg={selectedProgramBg}
                  onClick={() => handleSelectPending(item.id)}
                >
                  <Text styledTextProps={{ fontSize: '16px', fontWeight: isSelected ? 700 : 500 }}>
                    {name}
                  </Text>
                </PendingItem>
              );
            })}
            <StickySectionHeader data-sticky-header>Programs</StickySectionHeader>
          </>
        )}
        {programsContent}
      </>
    );
  };

  let mainContent;
  if (selectedPendingImport) {
    const pendingName = selectedPendingImport.programName
      || selectedPendingImport.originalProgramName
      || 'Untitled import';
    mainContent = (
      <>
        <HeaderContainer>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
              {pendingName}
            </Text>
          </InfoWrapper>
        </HeaderContainer>
        <PendingDetailCard>
          <PendingDetailWrapper>
            <PendingBanner>
              <PendingBannerBody>
                This is an unfinished import. The next step is to review and confirm
                the exercises in the import.
              </PendingBannerBody>
            </PendingBanner>
            <PendingButtonRow>
              <PendingDeleteButton
                type='button'
                onClick={handleAbandonPending}
                disabled={isAbandoning}
              >
                {/* Trash glyph — theme/error/emphasis (#FF0505) per Figma. */}
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                  <path
                    d='M2 4 H14 M5.33 4 V2.67 a1 1 0 0 1 1-1 h3.33 a1 1 0 0 1 1 1 V4 M3.33 4 L4 13.33 a1 1 0 0 0 1 1 h6 a1 1 0 0 0 1-1 L12.67 4 M6.67 7.33 V12 M9.33 7.33 V12'
                    stroke='#FF0505'
                    strokeWidth='1.33'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                Delete
              </PendingDeleteButton>
              <PendingReviewButton
                type='button'
                onClick={handleReviewPending}
                disabled={isLoadingHistoryDetail}
              >
                Review
                {/* Right arrow — theme/fg/on-accent (#FFFFFF) per Figma. */}
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                  <path
                    d='M3 8 H13 M9 4 L13 8 L9 12'
                    stroke='#FFFFFF'
                    strokeWidth='1.67'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </PendingReviewButton>
            </PendingButtonRow>
          </PendingDetailWrapper>
        </PendingDetailCard>
      </>
    );
  } else if (selectedProgram) {
    const showImportSuccess = lastImportedProgramId
      && String(lastImportedProgramId) === String(selectedProgram.id);
    mainContent = (
      <>
        {showImportSuccess && (
          <ImportSuccessBanner>
            <ImportSuccessBannerBody>
              Your file was imported successfully. Here is your new program!
            </ImportSuccessBannerBody>
          </ImportSuccessBanner>
        )}
        <HeaderContainer>
          <InfoWrapper>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
              {selectedProgram.programName}
            </Text>
            {manageUsers && (
              <Button
                href={`${process.env.PHP_APP_URL
                || 'https://app.teambuildr.com'}/exercises?action=savedPrograms&editProgramId=${selectedProgram.id}`}
                styledWrapperProps={{ paddingLeft: '8px', marginTop: '4px' }}
                name='Settings'
                fontSize='16px'
                styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
                txtColor='#77A7FF'
                bgColor='transparent'
                noBorder
              />
            )}
          </InfoWrapper>
          <ButtonWrapper>
            {/* Load to Calendar — white outlined per Figma 7106:3150.
                FlipIconWrap rotates the `download` icomoon glyph 180° on
                the Y-axis so it reads as an upload arrow (bar on top,
                arrow pointing up into the calendar). */}
            <FlipIconWrap>
              <Button
                name='Load to Calendar'
                icon='download'
                iconColor='#333'
                iconSize={16}
                customBorderRadius='4px'
                bgColor='white'
                borderColor='#ddd'
                txtColor='#333'
                fontSize='16px'
                onClick={handleOpenLoadCalendar}
                styledWrapperProps={{
                  boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                  padding: '6px 16px',
                }}
                styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
              />
            </FlipIconWrap>
            {/* Edit Program — dark filled per Figma 7106:3150. */}
            <Button
              name='Edit Program'
              icon='edit-workout'
              iconColor='#fff'
              iconSize={16}
              customBorderRadius='4px'
              noBorder
              bgColor='#444'
              txtColor='#fff'
              fontSize='16px'
              onClick={handleEditProgram}
              styledWrapperProps={{
                boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                padding: '6px 16px',
              }}
              styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
            />
          </ButtonWrapper>
        </HeaderContainer>

        <NavigatorPreviewPanel
          type='program'
          label
          maxWidth='1000px'
          previewCopy={renderProgramsInfo({ showTitle: false })}
        />
      </>
    );
  } else if (programsError && !isLoading) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
          Error loading programs
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {programsError}
        </Text>
      </EmptyState>
    );
  } else if (isInvalidProgramId) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          Program could not be found
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          The program ID in the URL does not exist in this account.
        </Text>
      </EmptyState>
    );
  } else {
    mainContent = (
      <EmptyState>
        {renderProgramsInfo()}
      </EmptyState>
    );
  }

  if (isMobile) {
    if (programId) {
      return (
        <MobilePage>
          <MobileHeader>
            <MobileBackLink
              type='button'
              onClick={() => history.push('/dashboard/programs')}
            >
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              Back to Programs
            </MobileBackLink>
          </MobileHeader>
          <MobileDetailContainer>
            {mainContent}
          </MobileDetailContainer>
        </MobilePage>
      );
    }
    return (
      <MobilePage>
        <MobileHeader>
          <MobileBackLink
            type='button'
            onClick={() => history.push('/dashboard')}
          >
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            Back to Home
          </MobileBackLink>
          <MobileSectionTitle>Programs</MobileSectionTitle>
        </MobileHeader>
        <NavigatorSelectorPanel
          fullWidth
          buttons={selectorButtons}
          searchPlaceholder='Search programs'
          isLoading={isLoading}
          selectedId={selectedProgram?.id}
          restoreSelectedScrollRef={false}
          renderList={renderProgramList}
        />
      </MobilePage>
    );
  }

  return (
    <Container>
      <NavigatorSelectorPanel
        width={285}
        buttons={selectorButtons}
        searchPlaceholder='Search programs'
        isLoading={isLoading}
        selectedId={selectedProgram?.id}
        restoreSelectedScrollRef={Boolean(programId)}
        renderList={renderProgramList}
      />
      <Section>
        {mainContent}
      </Section>
      {calPickerOpen && (
        <CalPickerOverlay onClick={() => setCalPickerOpen(false)}>
          <CalPickerBox onClick={(e) => e.stopPropagation()}>
            <CalPickerHeader>
              <CalPickerTitleRow>
                <CalPickerTitle>Load to Calendar</CalPickerTitle>
                <CalPickerClose
                  type='button'
                  aria-label='Close'
                  onClick={() => setCalPickerOpen(false)}
                >
                  {/* Close glyph — Eva / Close pattern from Figma node 7112:3453. */}
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                    <path
                      d='M4 4 L12 12 M12 4 L4 12'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                  </svg>
                </CalPickerClose>
              </CalPickerTitleRow>
              <CalPickerSubtitle>
                Turn this program into a scheduled workout by loading it into an existing or new
                calendar.
              </CalPickerSubtitle>
            </CalPickerHeader>

            <CalPickerField>
              <CalPickerLabel htmlFor='cal-picker-select'>Select a calendar</CalPickerLabel>
              <CalPickerComboboxWrap
                ref={calComboboxRef}
                isOpen={calDropdownOpen}
              >
                <CalPickerSearchIcon aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <circle cx='11' cy='11' r='6.5' stroke='currentColor' strokeWidth='2' />
                    <path d='M16 16 L20 20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                  </svg>
                </CalPickerSearchIcon>
                <CalPickerComboboxInput
                  id='cal-picker-select'
                  type='text'
                  autoComplete='off'
                  placeholder='Select a calendar'
                  value={calSearch}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setCalDropdownOpen(true)}
                  onClick={() => setCalDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setCalDropdownOpen(false);
                    }
                  }}
                />
                <CalPickerChevron
                  isOpen={calDropdownOpen}
                  aria-hidden='true'
                  onClick={() => setCalDropdownOpen((open) => !open)}
                >
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M7 10 L12 15 L17 10'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </CalPickerChevron>
                {calDropdownOpen && (
                  <CalPickerDropdown>
                    {isLoadingPlanCalendars && (
                      <CalPickerDropdownEmpty>Loading calendars…</CalPickerDropdownEmpty>
                    )}
                    {!isLoadingPlanCalendars
                      && filteredCalOptions.account.length === 0
                      && filteredCalOptions.athlete.length === 0 && (
                        <CalPickerDropdownEmpty>
                          No calendars match your search
                        </CalPickerDropdownEmpty>
                    )}
                    {filteredCalOptions.account.length > 0 && (
                      <>
                        <CalPickerDropdownGroupLabel>Calendars</CalPickerDropdownGroupLabel>
                        {filteredCalOptions.account.map((opt) => (
                          <CalPickerDropdownOption
                            key={opt.key}
                            isFocused={selectedCalKey === opt.key}
                            onClick={() => handlePickCalendar(opt)}
                          >
                            {opt.label}
                          </CalPickerDropdownOption>
                        ))}
                      </>
                    )}
                    {filteredCalOptions.athlete.length > 0 && (
                      <>
                        <CalPickerDropdownGroupLabel>Athlete Calendars</CalPickerDropdownGroupLabel>
                        {filteredCalOptions.athlete.map((opt) => (
                          <CalPickerDropdownOption
                            key={opt.key}
                            isFocused={selectedCalKey === opt.key}
                            onClick={() => handlePickCalendar(opt)}
                          >
                            {opt.label}
                          </CalPickerDropdownOption>
                        ))}
                      </>
                    )}
                  </CalPickerDropdown>
                )}
              </CalPickerComboboxWrap>
            </CalPickerField>

            <CalPickerField>
              <CalPickerLabel htmlFor='cal-picker-new'>Or create a new calendar</CalPickerLabel>
              <CalPickerInput
                id='cal-picker-new'
                placeholder='Add a calendar title'
                value={newCalName}
                onChange={(e) => handleTypeNew(e.target.value)}
              />
            </CalPickerField>

            {calError && <CalPickerError>{calError}</CalPickerError>}

            <CalPickerFooter>
              <CalPickerCancel type='button' onClick={() => setCalPickerOpen(false)}>
                Cancel
              </CalPickerCancel>
              <CalPickerContinue
                type='button'
                onClick={handleContinue}
                disabled={!selectedCalKey && !newCalName.trim()}
              >
                Continue
                {/* Arrow icon — 16x16 right-arrow per Figma node 7119:5807. */}
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                  <path
                    d='M3 8 H13 M9 4 L13 8 L9 12'
                    stroke='currentColor'
                    strokeWidth='1.67'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </CalPickerContinue>
            </CalPickerFooter>
          </CalPickerBox>
        </CalPickerOverlay>
      )}
      {pendingDeleteId !== null && (
        <CalPickerOverlay
          onClick={(e) => {
            // Backdrop click cancels — the inner card stops propagation so
            // clicking the dialog body itself doesn't dismiss.
            if (e.target === e.currentTarget && !isAbandoning) {
              handleCancelDelete();
            }
          }}
        >
          <ConfirmDeleteCard onClick={(e) => e.stopPropagation()}>
            <ConfirmDeleteTitle>Delete unfinished import?</ConfirmDeleteTitle>
            <ConfirmDeleteBody>
              This will permanently remove the unfinished import.
              You&rsquo;ll need to upload your spreadsheet again to retry.
            </ConfirmDeleteBody>
            <ConfirmDeleteFooter>
              <ConfirmDeleteCancel
                type='button'
                onClick={handleCancelDelete}
                disabled={isAbandoning}
              >
                Cancel
              </ConfirmDeleteCancel>
              <ConfirmDeleteConfirm
                type='button'
                onClick={handleConfirmDelete}
                disabled={isAbandoning}
              >
                {isAbandoning ? 'Deleting…' : 'Delete'}
              </ConfirmDeleteConfirm>
            </ConfirmDeleteFooter>
          </ConfirmDeleteCard>
        </CalPickerOverlay>
      )}
    </Container>
  );
};

ProgramNavigator.propTypes = {
  accountCode: PropTypes.number,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  manageUsers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

ProgramNavigator.defaultProps = {
  accountCode: undefined,
  programId: undefined,
  manageUsers: false,
  isMobile: false,
};

export default ProgramNavigator;
